import { CENTER_REFRESH_TOKEN_KEY, CENTER_TOKEN_KEY } from "@/constants/common";
import { removeAxiosAuth, setAxiosAuth } from "@/utils/api";
import { toNumber } from "@/utils/number";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

// Persist auth cookies (instead of session cookies) so the session survives
// closing the tab/browser. Matches the Cognito refresh-token validity (~30d);
// an expired access token is still recoverable via the refresh token on load.
const AUTH_COOKIE_EXPIRES_DAYS = toNumber(process.env.REACT_APP_AUTH_COOKIE_EXPIRES_DAYS) || 30;
const isDevMode = process.env.REACT_APP_APP_ENV === "DEV";

/**
 * Sets the access and refresh tokens in cookies and updates the Axios authentication configuration.
 *
 * @param {string} accessToken - The access token to set.
 * @param {string} refreshToken - The refresh token to set.
 */
export const setToken = (accessToken: string, refreshToken: string): void => {
  const options: Cookies.CookieAttributes = { expires: AUTH_COOKIE_EXPIRES_DAYS };

  if (!isDevMode) {
    options.secure = true;
    options.sameSite = "strict";
  }
  Cookies.set(CENTER_TOKEN_KEY, accessToken, options);
  Cookies.set(CENTER_REFRESH_TOKEN_KEY, refreshToken, options);
  setAxiosAuth(); // Updates Axios instance with new tokens for authentication
};

/**
 * Removes the access and refresh tokens from cookies and updates the Axios authentication configuration.
 */
export const removeToken = (): void => {
  Cookies.remove(CENTER_TOKEN_KEY);
  Cookies.remove(CENTER_REFRESH_TOKEN_KEY);
  removeAxiosAuth(); // Updates Axios instance to remove authentication
};

export const getToken = (): { accessToken: string | undefined; refreshToken: string | undefined } => {
  return {
    accessToken: Cookies.get(CENTER_TOKEN_KEY),
    refreshToken: Cookies.get(CENTER_REFRESH_TOKEN_KEY),
  };
};

/**
 * Verifies if a given token is still valid based on its expiration time.
 *
 * @param {string} token - The token to verify.
 * @returns {boolean} - `true` if the token is still valid, `false` otherwise.
 */
export const verifyToken = (token: string): boolean => {
  try {
    const decoded = jwtDecode<{ exp: number }>(token);
    // Check if the token's expiration time is greater than the current time
    return decoded.exp > new Date().getTime() / 1000;
  } catch (e) {
    // Return false if decoding fails
    return false;
  }
};

/**
 * Decode a JWT payload WITHOUT verifying its signature.
 * Frontend port of the backend `decodeJwtPayload` (jwt-claims.util.ts).
 */
export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const decoded = jwtDecode<Record<string, unknown>>(token);
    return decoded && typeof decoded === "object" ? decoded : null;
  } catch {
    return null;
  }
};

/**
 * Extract claim keys from a JWT into a string map.
 * Frontend port of the backend `claimExactKeys` (jwt-claims.util.ts): object/nullish
 * claims are skipped, everything else is coerced to string.
 *
 * @example
 * claimExactKeys(accessToken, ["sub", "organizationId"]) // => { sub: "...", organizationId: "..." }
 */
export const claimExactKeys = <K extends string>(token: string, keys: readonly K[]): Partial<Record<K, string>> => {
  try {
    const payload = decodeJwtPayload(token);
    if (!payload) return {};

    const claimed: Partial<Record<K, string>> = {};
    for (const key of keys) {
      const value = payload[key];
      if (value == null || typeof value === "object") continue;
      claimed[key] = String(value);
    }
    return claimed;
  } catch {
    return {};
  }
};

/**
 * Identity of the current session, read from the access token claims instead of
 * from dedicated cookies — the token is the single source of truth, so the email
 * / 組織ID can never drift from the session they belong to (e.g. after an
 * impersonation handoff or a login in another tab).
 */
export const getSessionIdentity = (): { sub?: string; email?: string; externalId?: string } => {
  const { accessToken } = getToken();
  if (!accessToken) return {};

  return claimExactKeys(accessToken, ["sub", "email", "externalId"]);
};
